import { InfoCircle, RefreshCCW02 } from "untitledui-js-base";
import { ReactNode } from "react";
import { Button, ButtonVariant, Show } from "shared/components";
import {
  ApplicationStatus,
  DataVendors,
  VerificationNames
} from "shared/types";
import { RerunRequirement } from "shared/types/verification";
import { ResumeTooltip } from "shared/app/OnHoldResume/ResumeTooltip";
import { ReportCompletedBanner } from "views/applications-id-report/components/ReportCompletedBanner";
import { Section } from "views/applications-id-report/components/Section";
import { getRerunRequirementMessage } from "views/applications-id-report/utils/getRerunRequirementMessage";
import { getCreditVendorName } from "../utils";

type NoCreditFileViewProps = {
  applicantName: string;
  applicationId: string | number;
  applicationStatus: ApplicationStatus;
  completedAt?: string | null;
  onRerunReport: () => void;
  rerunRequirements?: RerunRequirement[];
  vendor?: DataVendors;
  verificationId: number;
};

export const NoCreditFileView = ({
  applicantName,
  applicationId,
  applicationStatus,
  completedAt,
  onRerunReport,
  rerunRequirements,
  vendor,
  verificationId
}: NoCreditFileViewProps) => {
  const rerunRequirementMessage = getRerunRequirementMessage({
    requirement: rerunRequirements?.[0],
    reportName: "Credit report"
  });
  const isOnHold = applicationStatus === ApplicationStatus.OnHold;

  let rerunTooltip: ReactNode = "";
  if (rerunRequirementMessage) rerunTooltip = rerunRequirementMessage;
  else if (isOnHold)
    rerunTooltip = <ResumeTooltip applicationId={applicationId} />;

  return (
    <Section sectionTitle="Credit report summary" maxWidth="100%">
      <Show if={Boolean(completedAt)}>
        <ReportCompletedBanner
          verificationName={VerificationNames.CreditReport}
          completedAt={completedAt ?? undefined}
          reportId={String(verificationId)}
          vendor={vendor}
        />
      </Show>

      <div className="flex flex-col items-center gap-6 py-6 text-center">
        <div className="rounded-full bg-info-50 p-5">
          <InfoCircle size="24" className="text-info-700 h-6 w-6" />
        </div>

        <div className="max-w-[531px] space-y-2">
          <h2 className="text-lg font-bold text-content-1000">
            No credit file could be found for {applicantName}
          </h2>
          <p className="text-sm font-medium text-content-300">
            {getCreditVendorName(vendor)} returned a no-hit, which means no
            credit file matched the information we submitted. There is no credit
            data to review for this applicant.
          </p>
        </div>

        <div className="max-w-[531px] space-y-2 text-left">
          <p className="text-sm font-medium text-content-400">
            This usually happens when:
          </p>
          <ul className="list-disc space-y-1 pl-5 text-sm text-content-300">
            <li>The applicant has not built any credit history yet</li>
            <li>
              The name, date of birth, SSN or address we submitted does not
              match what the bureau has on file
            </li>
          </ul>
          <p className="text-sm text-content-300">
            Review the customer inquiry below to confirm the data we submitted,
            then re-run the report if anything needs to be corrected.
          </p>
        </div>

        <Button
          variant={ButtonVariant.BASE}
          Icon={RefreshCCW02}
          tooltip={rerunTooltip}
          tooltipDelay={250}
          tooltipPlacement="bottom"
          disabled={Boolean(rerunRequirements?.length) || isOnHold}
          onClick={onRerunReport}
        >
          Re-run report
        </Button>
      </div>
    </Section>
  );
};
